import constants from 'scoreboard/components/constants'
import formatValue from 'scoreboard/components/format-value'
import _ from 'lodash'
import { higherBetter } from 'scoreboard/components/thresholds-and-colors'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
const { metricish } = ScorecardNode
import config from 'scoreboard/components/config'
import {GRAY, GREEN_MEDIUM, RED_MEDIUM, SLATE} from './colors'

function empty () {
	return { direction: 'none', color: GRAY }
}

/*
 * Given a scorecard node and scores for the previous and current periods,
 * returns the trend/change data in the following format:
 *   {
 *		direction: 'none', // or 'up', 'down', 'level'
 *		color: GRAY, // or GREEN_MEDIUM, RED_MEDIUM, SLATE
 *      formatted: '+1.2' // or '-1.2', '+$1.2 USD', see https://github.com/SpiderStrategies/Scoreboard/issues/7600
 *   }
 */
function trend (sn, previous, current) {
	if (!previous || !current) {
		return empty()
	}

	if (!current.applicable || !previous.applicable) {
		return {
			direction: 'none',
			color: SLATE
		}
	}

	let scoringType = config.scoringType(sn.scoringTypeId)
	if (metricish(sn) &&
		_.get(scoringType, 'standardScoringType') != constants.YES_NO &&
		!scoringType.isCustomText) {
		return metricValueTrend(current, previous, scoringType, sn)
	} else {
		return scoreTrend(current, previous, sn, scoringType)
	}
}

function metricValueTrend(current, previous, scoringType, sn) {
	// If it's a non-yes/no metric, compare the actual values
	var currentVal = current.actual
	var previousVal = previous.actual

	// if no value for current or previous period, there's no trend to show
	if (!_.isNumber(previousVal) || !_.isNumber(currentVal)) {
		return empty()
	}

	var change = currentVal - previousVal

	// #19414 arrow direction for metrics is always based on the change in
	// actual value; whether the change is good or not is irrelevant
	let direction
	if (change === 0) {
		direction = 'level'
	} else {
		direction = change > 0 ? 'up' : 'down'
	}

	// #19414 arrow color is based on whether the change is good (green) or bad (red)
	let color
	if (_.get(scoringType, 'standardScoringType') == constants.UNSCORED) {
		color = SLATE
	} else if (!config.ignorePeriodsWithoutValues &&
		(_.get(previous, 'color', GRAY) == GRAY ||
			_.get(current, 'color', GRAY) == GRAY)) {
		color = GRAY
	} else if (_.get(scoringType, 'standardScoringType') == constants.STABILIZE_2 ||
		_.get(scoringType, 'standardScoringType') == constants.STABILIZE_3) {
		color = getStabilizeColor({change, previousVal, currentVal, current})
	} else {
		color = getNonStabilizeColor({change, sn, previous, current})
	}

	return {
		direction: direction,
		color: color,
		unformatted: change,
		formatted: (change < 0 ? '' : '+') + formatValue({
			value: change,
			dataType: sn.dataType,
			precision: sn.precision,
			currencyCode: sn.currencyCode
		})
	}
}

function scoreTrend(current, previous, sn, scoringType) {
	// non-metrics and yes/no metrics are easier; just compare the scores, higher is always better
	var currentScore = current.score
	var previousScore = previous.score
	var color, direction

	if (!_.isNumber(previousScore) || !_.isNumber(currentScore)) {
		return empty()
	}

	var change = currentScore - previousScore

	if (change === 0) {
		direction = 'level'
	} else {
		direction = change > 0 ? 'up' : 'down'
	}

	if (direction === 'level') {
		color = GRAY
	} else if (direction === 'up') {
		color = GREEN_MEDIUM
	} else if (direction === 'down') {
		color = RED_MEDIUM
	}

	var result = { direction, color }

	if (metricish(sn)) {
		if (_.get(scoringType, 'standardScoringType') != constants.YES_NO) {
			// #20916 If this is not a yes/no metric we might still need to format if
			// we are displaying scores as percents
			result.formatted = (change < 0 ? '' : '+') + formatValue({
				value: change,
				dataType: 'score' // #50184 respect the showScoresAsPercent flag
			})
		}
	} else {
		// we're letting yes/no metrics use the non-metric logic because
		// a simple score comparison works, but we don't want a formatted
		// change value for yes/no metrics
		result.unformatted = change
		result.formatted = (change < 0 ? '' : '+') + formatValue({
			value: change,
			dataType: 'score' // #48435 respect the showScoresAsPercent flag
			// use default database-configured precision for scores
		})
	}

	return result
}

function getStabilizeColor({change, previousVal, currentVal, current}) {
	// for the stabilize metrics, we can't just say higher is better or worse; it
	// depends on what side of the "best" threshold we're on

	// If the scores are the same, it's trickier to determine whether
	// the value is better or worse. Need to figure out where we are
	// relative to the "best" threshold (the one in the middle).

	let color = GRAY // if we can't figure things out, it'll just have to be considered unchanged

	if (current.thresholds) {
		var best = current.thresholds[parseInt(current.thresholds.length / 2) + 1]
		if (_.isNumber(best)) {
			if (change === 0) {
				color = GRAY
			} else if (previousVal > best && currentVal > best) {
				// If both values are above the "best" threshold, then
				// higher is worse (because we're getting further from
				// the "best" threshold in the middle)
				color = change < 0 ? GREEN_MEDIUM : RED_MEDIUM
			} else if (previousVal < best && currentVal < best) {
				// If both values are below the "best" threshold, then
				// lower is worse (because we're getting further from
				// the "best" threshold in the middle)
				color = change > 0 ? GREEN_MEDIUM : RED_MEDIUM
			}
		}
	}
	return color
}

function getNonStabilizeColor({change, sn, previous, current}) {
	// #23511 don't try to figure out colors based on scores to avoid
	// rounding issues

	// For non-stabilize and non-yes-no metrics, if the score is the same,
	// then we can decide whether the value change is good or bad based
	// on whether higher is better
	let higherIsBetter = higherBetter({
		model: sn,
		previousScore: previous,
		currentScore: current
	})

	if (change === 0 || _.isNil(higherIsBetter)) {
		return GRAY
	} else if (higherIsBetter) {
		return change > 0 ? GREEN_MEDIUM : RED_MEDIUM
	} else {
		return change > 0 ? RED_MEDIUM : GREEN_MEDIUM
	}
}

export default trend
export { scoreTrend }

trend.__filename__ = 'components/trend.js'
